/**
 * browser-loop — closed event-kind taxonomy.
 *
 * 45 values across 10 families. MUST stay in lock-step with the backend's
 * `BrowserEventKind` StrEnum in `backend/src/internal_ops/browser/enums.py`.
 *
 * The drift gate runs in CI:
 *   backend/tests/architecture/test_browser_substrate_drift.py
 *
 * It greps THIS file for every string literal matching the kind shape and
 * asserts a 1:1 correspondence with the Python enum. Adding a new kind
 * requires:
 *   1. Backend: enum entry + Pydantic model + service routing branch +
 *      translator switch (boot validator catches drift).
 *   2. Frontend: enum entry here + emitter call site somewhere in patches/.
 *   3. Same PR — closed-loop §III.10 boot-validator discipline.
 *
 * The 10 families are substrate-constant (closed-loop §III.8.1):
 *   console / network (fetch+sse+ws) / errors / navigation / interaction /
 *   lifecycle / web vitals / performance / lab-only / client-meta.
 */

export const BROWSER_EVENT_KIND = {
  // ── console (5) ────────────────────────────────────────────────
  console_log: "console.log",
  console_info: "console.info",
  console_warn: "console.warn",
  console_error: "console.error",
  console_debug: "console.debug",

  // ── network (9) ────────────────────────────────────────────────
  fetch_start: "fetch.start",
  fetch_complete: "fetch.complete",
  fetch_error: "fetch.error",
  sse_open: "sse.open",
  sse_event: "sse.event",
  sse_close: "sse.close",
  ws_open: "ws.open",
  ws_message: "ws.message",
  ws_close: "ws.close",

  // ── errors (3) ─────────────────────────────────────────────────
  js_error: "js.error",
  js_unhandled_rejection: "js.unhandled_rejection",
  react_error_boundary: "react.error_boundary",

  // ── navigation (4) ─────────────────────────────────────────────
  nav_pushstate: "nav.pushstate",
  nav_popstate: "nav.popstate",
  nav_replacestate: "nav.replacestate",
  nav_hard_reload: "nav.hard_reload",

  // ── interaction (6) — PII-sensitive, scrub before transmit ─────
  user_click: "user.click",
  user_focus: "user.focus",
  user_input: "user.input",
  user_submit: "user.submit",
  user_copy: "user.copy",
  user_paste: "user.paste",

  // ── lifecycle (3) ──────────────────────────────────────────────
  page_load: "page.load",
  page_visibility: "page.visibility",
  page_unload: "page.unload",

  // ── web vitals (5) — W3C Core Web Vitals names, substrate-constant
  vital_lcp: "vital.lcp",
  vital_inp: "vital.inp",
  vital_cls: "vital.cls",
  vital_fcp: "vital.fcp",
  vital_ttfb: "vital.ttfb",

  // ── performance (4) ────────────────────────────────────────────
  perf_long_task: "perf.long_task",
  perf_resource: "perf.resource",
  perf_mark: "perf.mark",
  perf_memory: "perf.memory",

  // ── lab-only (3) — agent_lab world (injected via page.addInitScript) ──
  lab_step: "lab.step",
  lab_assertion: "lab.assertion",
  lab_screenshot: "lab.screenshot",

  // ── client meta (3) — the loop observing itself ────────────────
  loop_started: "loop.started",
  loop_flushed: "loop.flushed",
  loop_dropped: "loop.dropped",
} as const;

export type BrowserEventKind =
  (typeof BROWSER_EVENT_KIND)[keyof typeof BROWSER_EVENT_KIND];

export const ALL_KINDS: readonly BrowserEventKind[] = Object.values(
  BROWSER_EVENT_KIND,
);

export const SEVERITY = {
  debug: "debug",
  info: "info",
  warn: "warn",
  error: "error",
  critical: "critical",
} as const;

export type BrowserSeverity = (typeof SEVERITY)[keyof typeof SEVERITY];
